import { REGIONS } from 'v2/configs/constants'
import type { SectionTab } from 'v2/shared/components/section-tabs'
import { canViewUcc, isSupportOnly, isUccReviewerOnly } from 'v2/shared/utils/role-based-visibility'
import type { UserRole } from 'v2/types/api/auth'
import type { ModulePermissionTypeMap } from 'v2/types/rbac'

// Every tab the Phone Numbers page can show. The page rejects a URL tab outside this list, so a
// new tab must be added here as well as to the tab strip.
export const PHONE_NUMBERS_TABS = [
  'list',
  'unrented',
  'caller-id',
  'sender-id',
  'compliance',
  'port-in',
  'rental-summary',
  'lookup',
  'other-settings',
  'ucc',
  'sandbox-numbers',
] as const
export type PhoneNumbersTab = (typeof PHONE_NUMBERS_TABS)[number]

export const PHONE_NUMBERS_TAB_COPY: Record<PhoneNumbersTab, SectionTab> = {
  list: {
    title: 'Purchased numbers',
    description: 'Manage the phone numbers connected to your applications and agents.',
  },
  compliance: { title: 'KYC' },
  'sandbox-numbers': { title: 'Verified destinations' },
  'port-in': {
    title: 'Port-in',
    description: 'Transfer existing numbers to Plivo and track port-in orders.',
  },
  'caller-id': {
    title: 'Verified caller IDs',
    description: 'Manage external numbers verified for outbound caller ID.',
  },
  'sender-id': { title: 'Sender IDs' },
  'rental-summary': {
    title: 'Rental summary',
    description: 'Review number rental charges and deduction dates.',
  },
  lookup: {
    title: 'Lookup',
    description: 'Look up a number’s country, carrier, and other details.',
  },
  unrented: {
    title: 'Unrented numbers',
    description: 'Review recently released numbers and reclaim eligible numbers.',
  },
  'other-settings': { title: 'Other settings' },
  ucc: { title: 'UCC' },
}

export type PhoneNumbersTabAccess = {
  isIndianRegion: boolean
  isUSRegion: boolean
  isUccOnlyUser: boolean
  canViewUccTab: boolean
  showSandboxTab: boolean
  /** `undefined` while the access check is still loading. */
  hasSenderIdAccess: boolean | undefined
}

/** The part of the access that depends only on who the session is, not on loading queries. */
export type PhoneNumbersRoleAccess = Pick<
  PhoneNumbersTabAccess,
  'isIndianRegion' | 'isUSRegion' | 'isUccOnlyUser' | 'canViewUccTab'
>

// Support-only and UCC-reviewer-only sessions are limited to the UCC tab.
export const phoneNumbersRoleAccess = ({
  region,
  roles,
  permissionsMap,
}: {
  region?: string
  roles: UserRole[]
  permissionsMap?: ModulePermissionTypeMap
}): PhoneNumbersRoleAccess => {
  const isIndianRegion = region === REGIONS.IN
  return {
    isIndianRegion,
    isUSRegion: region === REGIONS.US,
    isUccOnlyUser: isSupportOnly({ roles }) || isUccReviewerOnly({ roles }),
    canViewUccTab: canViewUcc({ isIndianRegion, permissionsMap }),
  }
}

/** `pending`: the access check is still loading, so the tab is neither shown nor redirected away. */
export type PhoneNumbersTabState = 'offered' | 'hidden' | 'pending'

const isPhoneNumbersTab = (tab: string): tab is PhoneNumbersTab =>
  (PHONE_NUMBERS_TABS as readonly string[]).includes(tab)

const offeredIf = (condition: boolean): PhoneNumbersTabState => (condition ? 'offered' : 'hidden')

// The one rule for which tabs this account gets: the tab strip, the tab bodies and the URL guard all
// read it. Any string that is not a tab is hidden.
export const phoneNumbersTabState = (
  tab: string,
  a: PhoneNumbersTabAccess,
): PhoneNumbersTabState => {
  if (!isPhoneNumbersTab(tab)) return 'hidden'
  if (tab === 'ucc') return offeredIf(a.canViewUccTab)
  if (a.isUccOnlyUser) return 'hidden'
  switch (tab) {
    case 'compliance':
      return offeredIf(a.isIndianRegion)
    case 'port-in':
    case 'other-settings':
      return offeredIf(a.isUSRegion)
    case 'caller-id':
      return offeredIf(!a.isIndianRegion)
    case 'sandbox-numbers':
      return offeredIf(a.showSandboxTab)
    case 'sender-id':
      return a.hasSenderIdAccess === undefined ? 'pending' : offeredIf(a.hasSenderIdAccess)
    default:
      return 'offered'
  }
}
